import {
  CAPTION_PNG_H,
  CAPTION_PNG_W,
  CAPTION_SHADE_ALPHA,
  captionShadeColor,
  captionStyleById,
  captionStyleFont,
  type CaptionStyle,
} from "@fiket/booth-schema";
import type { Language } from "../../i18n/locale";
import { captionStyleLayout, noteGrainSpeckles } from "./captionStyleLayout";
// 순환 의존 방지: 글자 크기 계산은 captionFontSize.ts 로 분리해 captionStyleLayout ↔ captionCanvas 사이클을 끊는다.
import { captionFontPx as _captionFontPx } from "./captionFontSize";
export {
  captionFontPx,
  CAPTION_FONT_PX_MAX,
  CAPTION_FONT_PX_MIN,
  CAPTION_FONT_FULL_SIZE_GRAPHEMES,
} from "./captionFontSize";
// Playwright 테스트가 booth-schema 를 직접 임포트하지 않아도 되도록 re-export
export { captionStyleById } from "@fiket/booth-schema";

/**
 * 영상 자막 PNG 렌더 (BOOTH-85) — 키오스크가 캔버스로 글자를 그려 투명 PNG 로 올리고 서버 ffmpeg 는 얹기만 한다.
 * 서버에 CJK 폰트를 두지 않아도 4개 언어가 자동 해결되고, 미리보기(`.caption-overlay`)와 결과물이 같은 서체·크기·그늘이다.
 * `captionFontPx` 는 순수(vitest), `renderCaptionPng` 만 DOM(canvas·document.fonts) 을 쓴다.
 * 글자 크기 상수·함수는 captionFontSize.ts 에 위치하고 여기서 re-export 한다.
 */

/** 둥근 사각형 경로를 만든다. roundRect 미지원(Safari/iPadOS 16.4 미만)은 직각으로 폴백 */
function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath();
  if (typeof ctx.roundRect === "function" && r > 0) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.rect(x, y, w, h);
  }
}

/** 둥근 사각형 채우기 (roundRect 폴백 포함) */
function fillRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fill();
}

/** 둥근 사각형 외곽선 (roundRect 폴백 포함) */
function strokeRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.stroke();
}

/**
 * 자막 PNG 렌더 — `CAPTION_PNG_W×H`(1440×480) 투명 캔버스에 스타일별 그늘·박스·장식·글자를 그려 dataURL 로 돌려준다.
 * 렌더 순서: shade(회전 없음) → **단일 rotate 컨텍스트** 안에서 note(종이·결·테이프) 또는 box(tag·pill)
 * → rules/hairline(장식선) → ornaments(✦)·seal(낙관) → 글자.
 * glow 는 shadowBlur 36·color glowColor 로 두 번, shadow 는 blur 24·offsetY 6·rgba(0,0,0,.5).
 * 글자 채움은 `layout.fill` 이 있으면 그라데이션(끝점은 PNG 폭이 아니라 측정한 글자 범위), 없으면 단색.
 * `captionStyleById("cinema")` 를 기본값으로 넘기면 기존 cinema 동작과 동일하다.
 * 캔버스를 못 만들면 throw — 호출자가 자막 없이 진행한다.
 */
export async function renderCaptionPng(
  text: string,
  language: Language,
  style: CaptionStyle = captionStyleById("cinema")
): Promise<string> {
  // 레이아웃 계산을 먼저 해야 폰트 문자열을 얻을 수 있어 canvas 를 임시로 쓴다
  // → load 전에도 font 문자열은 알 수 있으므로 measure 없이 문자열만 조립
  const tempFont = captionStyleFont(style, language, _captionFontPx(text) * 2);
  try {
    const loadText = style.decoration === "ornaments" ? text + "✦" : text;
    await document.fonts?.load(tempFont, loadText);
  } catch {
    // 폰트 로드 실패는 치명적이지 않다
  }

  const canvas = document.createElement("canvas");
  canvas.width = CAPTION_PNG_W;
  canvas.height = CAPTION_PNG_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("캔버스 2D 컨텍스트를 만들 수 없어요");

  // measure 함수 — 실제 캔버스를 사용
  const measure = (font: string, t: string): number => {
    ctx.font = font;
    return ctx.measureText(t).width;
  };

  const layout = captionStyleLayout(text, language, style, measure);

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // ── 1. shade(그라데이션 배경) — 회전 없이 PNG 전체 ─────────────────────────
  if (layout.shade) {
    const shade = ctx.createLinearGradient(0, 0, 0, canvas.height);
    shade.addColorStop(0, captionShadeColor(0));
    shade.addColorStop(1, captionShadeColor(CAPTION_SHADE_ALPHA));
    ctx.fillStyle = shade;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  // ── 2. 단일 rotate 컨텍스트 — 박스·장식·글자가 함께 기울어야 한다 ───────────
  ctx.save();
  if (layout.rotateDeg !== 0) {
    ctx.translate(layout.x, layout.y);
    ctx.rotate((layout.rotateDeg * Math.PI) / 180);
    ctx.translate(-layout.x, -layout.y);
  }

  // ── 3. note(포스트잇 메모지) 또는 box(tag · pill) ──────────────────────────
  if (layout.box && layout.note) {
    const { x: bx, y: by, w: bw, h: bh, radius, color: bc } = layout.box;
    const { shadow, grain, tape } = layout.note;

    // 종이 + 그림자
    ctx.save();
    ctx.shadowColor = shadow.color;
    ctx.shadowBlur = shadow.blur;
    ctx.shadowOffsetY = shadow.offsetY;
    ctx.fillStyle = bc;
    fillRoundRect(ctx, bx, by, bw, bh, radius);
    ctx.restore();

    // 종이 결 — 메모지 안쪽으로 클립해 밖으로 새지 않게
    ctx.save();
    roundRectPath(ctx, bx, by, bw, bh, radius);
    ctx.clip();
    ctx.fillStyle = grain.color;
    for (const s of noteGrainSpeckles(layout.box, grain.seed, grain.count)) {
      ctx.globalAlpha = s.alpha;
      ctx.fillRect(s.x, s.y, s.size, s.size);
    }
    ctx.restore();

    // 마스킹 테이프 — 메모지 위쪽 가운데에 비스듬히
    ctx.save();
    ctx.translate(tape.cx, tape.cy);
    ctx.rotate((tape.rotateDeg * Math.PI) / 180);
    ctx.fillStyle = tape.color;
    ctx.fillRect(-tape.w / 2, -tape.h / 2, tape.w, tape.h);
    ctx.restore();
  } else if (layout.box) {
    const { x: bx, y: by, w: bw, h: bh, radius, color: bc } = layout.box;
    ctx.fillStyle = bc;
    fillRoundRect(ctx, bx, by, bw, bh, radius);
  }

  // ── 4. rules(포스터 위아래 장식선) · hairline(minimal 위 가는 선) ──────────
  if (layout.rules) {
    const { x1, x2, y1, y2, color: rc } = layout.rules;
    ctx.strokeStyle = rc;
    ctx.lineWidth = 2;
    // 위 선
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y1);
    ctx.stroke();
    // 아래 선
    ctx.beginPath();
    ctx.moveTo(x1, y2);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  if (layout.hairline) {
    const { x1, x2, y: hy, color: hc } = layout.hairline;
    ctx.strokeStyle = hc;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x1, hy);
    ctx.lineTo(x2, hy);
    ctx.stroke();
  }

  // ── 5. ornaments(gilt 양옆 ✦) · seal(brush 끝 낙관) ───────────────────────
  if (layout.ornaments) {
    const orn = layout.ornaments;
    ctx.font = orn.font;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = orn.color;
    ctx.fillText(orn.glyph, orn.left.x, orn.left.y);
    ctx.fillText(orn.glyph, orn.right.x, orn.right.y);
  }

  if (layout.seal) {
    const { x: sx, y: sy, size, radius, color: sc, borderColor, borderWidth } = layout.seal;
    ctx.fillStyle = sc;
    fillRoundRect(ctx, sx, sy, size, size, radius);
    // 인주 안쪽에 종이색 테두리 — 전각(篆刻) 느낌
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    const inset = borderWidth * 1.5;
    strokeRoundRect(ctx, sx + inset, sy + inset, size - inset * 2, size - inset * 2, radius * 0.6);
  }

  // ── 6. 글자 ────────────────────────────────────────────────────────────────
  ctx.font = layout.font;
  ctx.textBaseline = "middle";
  ctx.textAlign = layout.align;

  // effect 설정
  if (layout.effect === "shadow") {
    ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 6;
  } else if (layout.effect === "glow" && layout.glowColor) {
    // glow 는 두 번 그려 강하게 만든다
    ctx.shadowColor = layout.glowColor;
    ctx.shadowBlur = 36;
    ctx.shadowOffsetY = 0;
  }

  // letterSpacing 설정 (ctx.letterSpacing 지원 여부 확인)
  const ctxWithSpacing = ctx as unknown as { letterSpacing?: string };
  if (layout.letterSpacingPx > 0) {
    if (typeof ctxWithSpacing.letterSpacing !== "undefined") {
      ctxWithSpacing.letterSpacing = `${layout.letterSpacingPx}px`;
    }
  }

  // 글자 채움 — 그라데이션 끝점은 레이아웃이 계산한 글자 범위(회전 전 로컬 좌표)
  if (layout.fill) {
    const grad = ctx.createLinearGradient(
      layout.fill.x0,
      layout.fill.y0,
      layout.fill.x1,
      layout.fill.y1
    );
    const n = layout.fill.colors.length;
    layout.fill.colors.forEach((c, i) => {
      grad.addColorStop(n > 1 ? i / (n - 1) : 0, c);
    });
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = layout.color;
  }

  if (layout.effect === "glow" && layout.glowColor) {
    // 첫 번째 glow 패스
    ctx.fillText(text, layout.x, layout.y);
    // 두 번째 패스로 glow 강화 (blur 36 유지 — 브리프 지정)
    ctx.shadowBlur = 36;
    ctx.fillText(text, layout.x, layout.y);
  } else if (layout.letterSpacingPx > 0 && typeof ctxWithSpacing.letterSpacing === "undefined") {
    // letterSpacing 미지원 — grapheme 단위로 나눠 그린다 (왼쪽 끝은 레이아웃의 textLeft)
    ctx.textAlign = "left";
    let curX = layout.textLeft;
    for (const g of [...text]) {
      ctx.fillText(g, curX, layout.y);
      curX += measure(layout.font, g) + layout.letterSpacingPx;
    }
  } else {
    ctx.fillText(text, layout.x, layout.y);
  }

  ctx.restore();
  return canvas.toDataURL("image/png");
}
